import { motion } from "motion/react";
import { useState } from "react";
import type { ReactNode } from "react";

import { DeleteAccountModal } from "@/components/dashboard/DeleteAccountModal";
import { BentoCard } from "@/components/ui/BentoCard";

interface SettingsContentProps {
  /** Agent brain picker (reasoning model), composed by the route (AgentSection). */
  agentSlot?: ReactNode;
  /** Credits card (balance + top-ups), composed by the route (CreditsSection). */
  creditsSlot?: ReactNode;
  /** Subscription plan cards, composed by the route (SubscriptionSection). */
  subscriptionSlot?: ReactNode;
  onDeleteAccount?: () => Promise<void>;
}

export function SettingsContent({
  agentSlot,
  creditsSlot,
  subscriptionSlot,
  onDeleteAccount,
}: SettingsContentProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirmDelete() {
    if (!onDeleteAccount) return;
    setIsDeleting(true);
    try {
      await onDeleteAccount();
    } catch {
      setIsDeleting(false);
    }
  }

  return (
    <div className="space-y-10">
      {/* ── Agent ── */}
      {agentSlot && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <h2 className="mb-4 text-xl font-bold uppercase tracking-wider text-black dark:text-white">
            Agent
          </h2>
          {agentSlot}
        </motion.section>
      )}

      {/* ── Credits ── */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
      >
        <h2 className="mb-4 text-xl font-bold uppercase tracking-wider text-black dark:text-white">
          Credits
        </h2>
        {creditsSlot}
      </motion.section>

      {/* ── Subscription ── */}
      <motion.section
        id="subscription-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="scroll-mt-8"
      >
        <h2 className="mb-4 text-xl font-bold uppercase tracking-wider text-black dark:text-white">
          Subscription
        </h2>
        {subscriptionSlot}
      </motion.section>

      {/* ── Account ── */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
      >
        <h2 className="mb-4 text-xl font-bold uppercase tracking-wider text-black dark:text-white">
          Account
        </h2>
        <BentoCard shadow className="p-8">
          <div className="mt-6">
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="w-full rounded-xl bg-red-600 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-red-700"
            >
              Delete Account
            </button>
          </div>
        </BentoCard>
      </motion.section>

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
